import moment from 'moment-timezone';
import { UTC_TIMEZONE } from './constants';
import { normalizeTimezone } from './timezone';
/**
 * Converts a timestamp (milliseconds since epoch) to a Moment object with optional timezone.
 * If no timezone is provided, uses local timezone.
 * If timezone is 'UTC', uses UTC.
 * Otherwise, converts the UTC timestamp to the specified timezone.
 *
 * @param timestamp - Timestamp in milliseconds since epoch
 * @param timezone - Optional timezone string (e.g., 'America/New_York', 'UTC')
 * @returns Moment object in the specified timezone
 * @throws Error if timestamp is invalid
 *
 * @example
 * ```ts
 * const moment = convertTimestampToMoment(1609459200000, 'UTC');
 * const momentTz = convertTimestampToMoment(1609459200000, 'America/New_York');
 * ```
 */
export const convertTimestampToMoment = (timestamp, timezone) => {
    const normalizedTimezone = normalizeTimezone(timezone);
    if (!normalizedTimezone) {
        return moment(timestamp);
    }
    if (normalizedTimezone === UTC_TIMEZONE) {
        return moment.utc(timestamp);
    }
    return moment.utc(timestamp).tz(normalizedTimezone);
};
/**
 * Converts a Date or Moment object to a Moment object with optional timezone.
 * If no timezone is provided, uses local timezone.
 * If timezone is 'UTC', converts to UTC.
 * Otherwise, interprets the date in the specified timezone.
 *
 * @param date - Date or Moment object to convert
 * @param timezone - Optional timezone string (e.g., 'America/New_York', 'UTC')
 * @returns Moment object in the specified timezone
 *
 * @example
 * ```ts
 * const moment = convertDateToMoment(new Date(), 'UTC');
 * const momentTz = convertDateToMoment(new Date(), 'America/New_York');
 * ```
 */
export const convertDateToMoment = (date, timezone) => {
    const normalizedTimezone = normalizeTimezone(timezone);
    if (!normalizedTimezone) {
        return moment(date);
    }
    if (normalizedTimezone === UTC_TIMEZONE) {
        return moment.utc(date);
    }
    return moment.tz(date, normalizedTimezone);
};
/**
 * Converts a date/time value from one timezone to another.
 * For timestamps (numbers), treats them as UTC and converts to target timezone.
 * For Date/Moment objects, interprets them in the source timezone and converts to target timezone.
 *
 * @param value - Timestamp (number) or Date/Moment object
 * @param fromTimezone - Source timezone string
 * @param toTimezone - Target timezone string
 * @returns Moment object in the target timezone
 * @throws Error if timezones are invalid or value is invalid
 *
 * @example
 * ```ts
 * const converted = convertToTimezone(1609459200000, 'UTC', 'America/New_York');
 * const converted2 = convertToTimezone(new Date(), 'Europe/London', 'Asia/Tokyo');
 * ```
 */
export const convertToTimezone = (value, fromTimezone, toTimezone) => {
    if (typeof value === 'number') {
        return moment.utc(value).tz(toTimezone);
    }
    const dateInOriginalTz = moment.tz(value, fromTimezone);
    return moment.tz({
        year: dateInOriginalTz.year(),
        month: dateInOriginalTz.month(),
        day: dateInOriginalTz.date(),
        hour: dateInOriginalTz.hour(),
        minute: dateInOriginalTz.minute(),
        second: dateInOriginalTz.second(),
        millisecond: dateInOriginalTz.millisecond(),
    }, toTimezone);
};
export const toUTC = (value, timezone) => {
    if (typeof value === 'number') {
        return moment.utc(value);
    }
    if (timezone) {
        const dateInTimezone = moment.tz(value, timezone);
        return dateInTimezone.utc();
    }
    return moment(value).utc();
};
export const fromUTC = (value, toTimezone) => {
    const utcMoment = moment.utc(value);
    if (toTimezone === UTC_TIMEZONE) {
        return utcMoment;
    }
    return utcMoment.tz(toTimezone);
};
//# sourceMappingURL=conversion.js.map